import { useCluster } from "in_queue/contexts/ClusterContext";
import {
    useAllAvailableProjects,
    useAllQueueProjects,
    useSpeculativeProjectsInCluster
} from "in_queue/contexts/ProjectDataContext";
import { isOnlyExclusionSupported } from "in_queue/types/configPerRegion";
import { isWithdrawnByDefault } from "in_queue/types/projectType";
import { useMemo } from "react";
import { useCapacityOnlyProjectIds } from "../ScenarioBuilder/useCapacityOnlyProjectIds";
import { SensitivitiesCsvRules } from "./sensitivitiesCsv";
import { getWholeProjectOnlyProjectIds } from "./wholeProjectOnly";

/** The rules a sensitivities CSV is read with in this cluster; undefined while projects load. */
export const useSensitivitiesCsvRules = ():
    | SensitivitiesCsvRules
    | undefined => {
    const cluster = useCluster();
    const availableProjects = useAllAvailableProjects();
    const queueProjects = useAllQueueProjects();
    const speculativeProjects = useSpeculativeProjectsInCluster();
    const capacityOnlyProjectIds = useCapacityOnlyProjectIds();
    const onlyExclusionSupported = isOnlyExclusionSupported(cluster);

    return useMemo(() => {
        if (
            availableProjects === "loading" ||
            queueProjects === "loading" ||
            speculativeProjects === "loading"
        ) {
            return undefined;
        }
        return {
            onlyExclusionSupported,
            validProjectIds: new Set(availableProjects.map((p) => p.projectId)),
            speculativeProjectIds: new Set(
                speculativeProjects.map((sp) => sp.projectId)
            ),
            priorQueueProjectIds: capacityOnlyProjectIds,
            wholeProjectOnlyProjectIds:
                getWholeProjectOnlyProjectIds(availableProjects),
            withdrawnProjectIds: new Set(
                availableProjects
                    .filter(isWithdrawnByDefault)
                    .map((p) => p.projectId)
            ),
            nonEditableProjectIds: new Set(
                queueProjects
                    .filter((p) => !p.isEditable)
                    .map((p) => p.projectId)
            )
        };
    }, [
        availableProjects,
        queueProjects,
        speculativeProjects,
        capacityOnlyProjectIds,
        onlyExclusionSupported
    ]);
};
